/**
 * Registry.ts
 * @author Diao Zheng
 * @file A standard registry for static objects
 */
import { __decorate } from "tslib";
import { ensures, requires } from "./Contract";
import * as Option from "./Option";
export class Registry {
    constructor(defaultKey, defaultValue, shouldProvideFallback) {
        this.registry = {};
        this.fallback = {};
        this.registeredKeys = new Set();
        this.getValueRecursive = (key) => {
            return Option.value_(this.getValue(key), () => Option.map(this.fallback[key], this.getValueRecursive));
        };
        this.mapValueRecursive = (key, f) => {
            return Option.value_(this.mapValue(key, f), () => Option.map(this.fallback[key], (fallback) => this.mapValueRecursive(fallback, f)));
        };
        this.DEFAULT_VALUE = defaultKey;
        this.shouldProvideFallback = shouldProvideFallback !== null && shouldProvideFallback !== void 0 ? shouldProvideFallback : true;
        if (Option.isSome(defaultValue)) {
            this.registry[defaultKey] = defaultValue;
        }
        this.registeredKeys.add(defaultKey);
    }
    addKey(key, fallback) {
        this.registeredKeys.add(key);
        if (Option.isSome(fallback)) {
            this.fallback[key] = fallback;
        }
        else if (this.shouldProvideFallback) {
            this.fallback[key] = this.DEFAULT_VALUE;
        }
    }
    updateValue(key, value) {
        this.registry[key] = value;
    }
    getValue(key) {
        return this.registry[key];
    }
    unsafeGetValue(key) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.registry[key];
    }
    mapValue(key, f) {
        return Option.map(this.getValue(key), f);
    }
    mutateValue(key, f) {
        const output = this.registry[key];
        if (Option.isSome(output)) {
            f(output);
            return true;
        }
        return false;
    }
    keys() {
        return this.registeredKeys[Symbol.iterator]();
    }
    UNSAFE_resetKeys() {
        this.registeredKeys.clear();
    }
}
__decorate([
    requires(function (key) {
        return !this.registeredKeys.has(key);
    }, "key not must be registered."),
    requires(function (_k, fallback) {
        return (!fallback) || this.registeredKeys.has(fallback);
    }, "fallback must be registered.")
], Registry.prototype, "addKey", null);
__decorate([
    requires(function (key, _v) {
        return this.registeredKeys.has(key);
    }, "key must be registered.")
], Registry.prototype, "updateValue", null);
__decorate([
    requires(function (key) {
        return this.registeredKeys.has(key);
    }, "key must be registered")
], Registry.prototype, "getValue", null);
__decorate([
    requires(function (key) {
        return this.registeredKeys.has(key);
    }, "key must be registered"),
    ensures((result) => Option.isSome(result), "value must be defined")
], Registry.prototype, "unsafeGetValue", null);
__decorate([
    requires(function (key, _f) {
        return this.registeredKeys.has(key);
    }, "key must be registered")
], Registry.prototype, "mutateValue", null);
